/** @prettier */

import format from '../define';

/** csvファイルデータ格納位置 */
const csvColNum = {
  time: 0,
  power: 1,
  mode: 2,
  setTemp: 3,
  fan: 4,
};

/**
 * 特定日スケジュールCSVのフォーマットをチェックしてAPI用のデータを生成する関数
 * @param readData チェックするデータ
 * アップロードCSV例
 * Time,Power,Mode,Set temperature,Fan speed
 * 1:00,on,auto,20,auto
 * 2:00,off,fan,15.5,low
 * @return エラーコード,パラメータ名,登録API用のデータ
 */
export const updateScheduleCSVData = (readData: string) => {
  //１行ずつ分解して配列にする
  let splitData = readData.split(/\n|\r\n/);
  //データ最後に改行がある場合は配列末尾に空文字ができてしまうため取り除く
  if (splitData[splitData.length - 1] === '') {
    splitData.pop();
  }

  //データがない場合はエラー
  if (splitData.length === 1) {
    return {
      errorCode: 'unregistered',
      parameter: ['Empty data'],
      processedData: '',
    };
  }

  //エクセル上で最終行を消すと、カンマのみの行が生成される場合がある。
  //エクセル上だと見落としがちなため、最終行からカンマのみの行を確認し、
  //存在する場合はその行をチェック対象から削除する。
  while (splitData.length > 0) {
    const lastLine = splitData[splitData.length - 1];
    const labelCount = Object.keys(csvColNum).length;
    const lastLineLabelCount = lastLine.split(',').length;

    //対象行の文字がカンマのみかつラベル数が一致している場合のみ削除する
    if (/^,+$/.test(lastLine) && lastLineLabelCount === labelCount) {
      splitData.pop();
    } else {
      //データが存在する行が来たらその時点で終了
      break;
    }
  }

  //18行以上入力データが存在する(上限：ラベル＋スケジュール16項目)
  if (splitData.length > 17) {
    return {
      errorCode: 'excess',
      parameter: ['Exceeded data'],
      processedData: '',
    };
  }
  //ラベル処理
  //ラベルが不正の場合はエラーを返す
  const labelData = splitData[0].split(',');
  if (checkLabelData(labelData) === false) {
    return {errorCode: 'unregistered', parameter: ['Label'], processedData: ''};
  }
  //ラベル削除
  const removedLabel = splitData.shift();

  //設定時刻一覧
  const timeList: string[] = [];
  //１行ずつデータをチェック
  for (const rowData of splitData) {
    //カンマでデータ分割
    const columnData = rowData.split(',');

    //スケジュール不正データチェック
    const scheduleCheckResult = scheduleDataCheck(columnData, timeList);
    if (scheduleCheckResult.length > 0) {
      return {
        errorCode: 'unregistered',
        parameter: scheduleCheckResult,
        processedData: '',
      };
    }
    //時刻一覧に追加
    timeList.push(columnData[csvColNum.time]);
  }

  const processedData = [removedLabel].concat(splitData).join('\n');

  return {errorCode: '', parameter: [], processedData: processedData};
};

//prettier-ignore
/**
 * ラベルチェック
 * @param data csvデータラベル
 * @returns ラベル値が 正常：true , 不正：false
 */
const checkLabelData = (data: string[]) => {
  //ラベル項目数のチェック
  if (data.length !== Object.keys(csvColNum).length)        return false;
  //ラベル値のチェック
  if (data[csvColNum.time]        !== 'Time')               return false;
  if (data[csvColNum.power]       !== 'Power')              return false;
  if (data[csvColNum.mode]        !== 'Mode')               return false;
  if (data[csvColNum.setTemp]     !== 'Set temperature')    return false;
  if (data[csvColNum.fan]         !== 'Fan speed')          return false;
  return true;
};

/**
 * スケジュールデータの値チェック
 * @param arrayData チェック対象スケジュールデータ
 * @returns 不正データ項目のリスト
 */
const scheduleDataCheck = (arrayData: string[], timeList: string[]) => {
  /** 不正データ項目のリスト */
  const dataNGList = [];
  let regex;

  //指令時刻のデータチェック
  if (arrayData.length === 5 && arrayData[csvColNum.time] === '') {
    //時刻のみが空欄
    dataNGList.push('Data lack(Time)');
  } else {
    regex = new RegExp(format.SCHEDULE_COMMAND_TIME_PATTERN_OK);
    if (regex.test(arrayData[csvColNum.time]) === false) {
      //時刻のパターンがNG
      dataNGList.push('Time');
    } else {
      //時刻の重複をチェック
      if (timeList.includes(arrayData[csvColNum.time])) {
        dataNGList.push('Time');
      }
      //時刻はあるが他設定がすべて空欄
      if (
        arrayData.length !== 5 ||
        (arrayData[csvColNum.power] === '' &&
          arrayData[csvColNum.mode] === '' &&
          arrayData[csvColNum.setTemp] === '' &&
          arrayData[csvColNum.fan] === '')
      ) {
        dataNGList.push('Data lack');
        return dataNGList;
      }
    }
  }

  //運転停止のデータチェック
  regex = new RegExp(format.SCHEDULE_RUN_STOP_PATTERN_OK);
  if (regex.test(arrayData[csvColNum.power]) === false) {
    dataNGList.push('Power');
  }

  //運転モードのデータチェック
  regex = new RegExp(format.SCHEDULE_OPERATION_MODE_PATTERN_OK);
  if (regex.test(arrayData[csvColNum.mode]) === false) {
    dataNGList.push('Mode');
  }

  //設定温度のデータチェック
  if (arrayData[csvColNum.mode] !== 'heating') {
    regex = new RegExp(format.SCHEDULE_PRESET_TEMPERATURE_PATTERN_OK);
    if (regex.test(arrayData[csvColNum.setTemp]) === false) {
      dataNGList.push('Set temperature');
    }
  } else {
    regex = new RegExp(format.SCHEDULE_PRESET_TEMPERATURE_HEATING_PATTERN_OK);
    if (regex.test(arrayData[csvColNum.setTemp]) === false) {
      dataNGList.push('Set temperature');
    }
  }

  //風量のデータチェック
  regex = new RegExp(format.SCHEDULE_AIR_FLOW_PATTERN_OK);
  if (regex.test(arrayData[csvColNum.fan]) === false) {
    dataNGList.push('Fan speed');
  }

  return dataNGList;
};
